import i18n from "@/i18n";
import SocketController from "@/services/Socket/socket.controller";
import { validateWithSchema } from "@/utils/dataValidators.zod";
import {
  setSessionShouldRegisterAccount,
  setSessionShouldUpdateAccount,
} from "@/utils/tokens.util";
import md5 from "md5";
import { z } from "zod";

export async function authenticate(refreshToken: string): Promise<
  | {
      rStatus: "success";
    }
  | {
      rStatus: "error";
      rData: string;
    }
> {
  if (!refreshToken) {
    return {
      rStatus: "error",
      rData: i18n.t("No auth token provided"),
    };
  }

  console.log(`Sending auth command`);
  try {
    const response = await SocketController.prototype.sendCommand(
      "refreshToken",
      { refreshToken: refreshToken },
      true
    );

    console.log(response);

    if (response.status === 200 || response.status === 202) {
      const authSchema = z.object({
        authToken: z.string(),
        refreshToken: z.string(),
        shouldUpdateAccount: z.boolean(),
        shouldRegisterAccount: z.boolean(),
      });

      const parsedResponse = validateWithSchema(
        response.data,
        authSchema,
        "Auth response"
      );

      if (!parsedResponse) {
        console.log(response);
        return {
          rStatus: "error",
          rData: i18n.t("Invalid auth response"),
        };
      }

      setSessionShouldUpdateAccount(parsedResponse.shouldUpdateAccount);
      setSessionShouldRegisterAccount(parsedResponse.shouldRegisterAccount);

      return {
        rStatus: "success",
      };
    }

    if (response.status === 0) {
      return { rStatus: "error", rData: i18n.t("Network error") };
    }

    return {
      rStatus: "error",
      rData: i18n.t("Authentication failed"),
    };
  } catch (error: unknown) {
    console.log(error);
    return { rStatus: "error", rData: i18n.t("Authentication failed") };
  }
}

export async function sendMagicLink(
  email: string
): Promise<{ rStatus: "success" } | { rStatus: "error"; rData: string }> {
  if (
    !email ||
    typeof email !== "string" ||
    email.length === 0 ||
    email.length > 150
  ) {
    return {
      rStatus: "error",
      rData: i18n.t("Invalid email address"),
    };
  }

  const base64Credentials = btoa(email);

  try {
    // Socket Request
    const response = await SocketController.prototype.sendCommand(
      "magicLink",
      {
        encodedEmail: base64Credentials,
      },
      true
    );

    // //!DELETE THIS LOG
    console.log("Response from magic link command", response);

    // Error Handling based on the response status
    if (
      !response ||
      response.status === undefined ||
      response.status === null
    ) {
      console.log("Magic Link No Response");
      return {
        rStatus: "error",
        rData: i18n.t("Magic Link No Response"),
      };
    }

    if (response.status === 200 || response.status === 202) {
      return {
        rStatus: "success",
      };
    }

    if (response.status === 400) {
      console.log("Invalid Magic Link Request - Bad Request");
      return {
        rStatus: "error",
        rData: i18n.t("Bad Request. Please contact support."),
      };
    }

    if (
      response.status === 204 ||
      response.status === 401 ||
      response.status === 403
    ) {
      console.log("Magic Link Unauthorized");
      return {
        rStatus: "error",
        rData: i18n.t("Login Unauthorised"),
      };
    }

    if (response.status === 500) {
      console.log("Magic Link Failed - Server Error");
      return {
        rStatus: "error",
        rData: i18n.t("Magic Link Server Error"),
      };
    }

    if (response.status === 0) {
      console.log("Magic Link No Response or timeout error - status 0");
      return {
        rStatus: "error",
        rData: i18n.t("Magic Link No Response"),
      };
    }

    if (response.status === 307) {
      console.log("New unverified user - status 307");
      return {
        rStatus: "error",
        rData: i18n.t(
          "Your account is not verified by admin yet. Please be patient."
        ),
      };
    }

    if (response.status === 308) {
      console.log("Blocked user - status 308");
      return {
        rStatus: "error",
        rData: i18n.t(
          "Your account has been blocked by admin. Please contact support."
        ),
      };
    }

    console.error(
      "Magic Link Unknown Response from Server - status: ",
      response.status
    );
    return {
      rStatus: "error",
      rData: i18n.t("Magic Link General Error Response"),
    };
  } catch (error) {
    console.log("Magic Link Socket Error as,", error);
    return {
      rStatus: "error",
      rData: i18n.t("Magic Link General Error Response"),
    };
  }
}

export async function sendLogin(
  email: string,
  password: string
): Promise<
  | {
      rStatus: "success";
      rData: {
        refreshToken: string;
        authToken: string;
      };
    }
  | {
      rStatus: "error";
      rData: string;
    }
> {
  const hashedPassword = md5(password);
  // Combine email and hashed password, then encode as base64
  const credentials = `${email}&${hashedPassword}`;
  const base64Credentials = btoa(credentials);
  console.log("Base64 credentials", base64Credentials);

  const response = await SocketController.prototype.sendCommand(
    "login",
    {
      loginToken: base64Credentials,
    },
    true
  );

  if (response.status === 200) {
    const authSchema = z.object({
      authToken: z.string(),
      refreshToken: z.string(),
    });

    const parsedResponse = validateWithSchema(
      response.data,
      authSchema,
      "Auth response"
    );

    if (!parsedResponse) {
      console.log(response);
      return {
        rStatus: "error",
        rData: i18n.t("Invalid auth response"),
      };
    }

    return {
      rStatus: "success",
      rData: {
        authToken: parsedResponse.authToken,
        refreshToken: parsedResponse.refreshToken,
      },
    };
  }

  if (response.status === 304) {
    console.log("Login User must verify email");
    return {
      rStatus: "error",
      rData: i18n.t(
        "Please use Magic Link for first login to verify your email."
      ),
    };
  }

  if (response.status === 400) {
    console.log("Login Bad Request");
    return {
      rStatus: "error",
      rData: i18n.t("Bad Request. Please contact support."),
    };
  }

  if (
    response.status === 204 ||
    response.status === 401 ||
    response.status === 403
  ) {
    console.log("Login Unauthorized");
    return {
      rStatus: "error",
      rData: i18n.t("Login Unauthorised"),
    };
  }

  if (response.status === 500) {
    console.log("Login Failed - Server Error");
    return {
      rStatus: "error",
      rData: i18n.t("Login Server Error"),
    };
  }

  if (response.status === 0) {
    console.log("Login No Response or timeout error - status 0");
    return {
      rStatus: "error",
      rData: i18n.t("Network error or timeout. Please try again later."),
    };
  }

  if (response.status === 307) {
    console.log("New unverified user - status 307");
    return {
      rStatus: "error",
      rData: i18n.t(
        "Your account is not verified by admin yet. Please be patient."
      ),
    };
  }

  if (response.status === 308) {
    console.log("Blocked user - status 308");
    return {
      rStatus: "error",
      rData: i18n.t(
        "Your account has been blocked by admin. Please contact support."
      ),
    };
  }

  console.log("Login command failed with status", response.status);

  return {
    rStatus: "error",
    rData: i18n.t("Login failed"),
  };
}

export async function sendRefreshTokens(authToken: string): Promise<
  | {
      rStatus: "success";
      rData: {
        refreshToken: string;
        authToken: string;
      };
    }
  | {
      rStatus: "error";
      rData: string;
    }
> {
  try {
    console.log("Sending refresh token command");
    const response = await SocketController.prototype.sendCommand(
      "refreshToken",
      {
        refreshToken: authToken,
      },
      true
    );
    console.log("Response from refresh token command", response);
    console.log("Sending refresh token command");

    if (response) {
      if (response.status === 200) {
        const authSchema = z.object({
          authToken: z.string(),
          refreshToken: z.string(),
        });
        try {
          const parsedResponse = validateWithSchema(
            response.data,
            authSchema,
            "Auth response"
          );

          if (!parsedResponse) {
            console.log(response);
            return {
              rStatus: "error",
              rData: i18n.t("Invalid auth response"),
            };
          }

          return {
            rStatus: "success",
            rData: {
              refreshToken: parsedResponse.refreshToken,
              authToken: parsedResponse.authToken,
            },
          };
        } catch (error) {
          console.log("Error validating refresh token response", error);
          return {
            rStatus: "error",
            rData: i18n.t("Invalid auth response"),
          };
        }
      } else {
        if (response.status === 0) {
          console.log("Refresh token command failed - timeout was hit");
        } else if (response.status === 401) {
          console.log(
            "Refresh token command failed - unauthorized - expired token"
          );
        } else if (response.status === 400) {
          console.log(
            "Refresh token command failed - bad token format or missing token. Token as",
            authToken
          );
        } else if (response.status === 500) {
          console.log(
            "Refresh token command failed - Server Error. Token sent: ",
            authToken
          );
        } else {
          console.log("Refresh token command failed - unknown error");
        }
        // final return
        return {
          rStatus: "error",
          rData: i18n.t("Refresh token failed"),
        };
      }
    } else {
      console.log("Refresh token command failed - no response was received");
      return {
        rStatus: "error",
        rData: i18n.t("Refresh token failed"),
      };
    }
  } catch (error) {
    console.log("Error occurred during refresh token send command as", error);
    return {
      rStatus: "error",
      rData: i18n.t("Refresh token failed"),
    };
  }
}
